import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { logError } from "@/lib/log";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Quote, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ShareExperience() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState({ quote: "", name: user?.name || "", role: "Registered Manager", org: user?.org_name || "" });
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(params.get("share") === "1");

  useEffect(() => { api.get("/me/testimonial").then((r) => setInfo(r.data)).catch((err) => logError("Testimonial info failed", err)); }, []);
  if (!info || (!info.requested && !open)) return null;

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/me/testimonial", form);
      setInfo((i) => ({ ...i, submitted: data }));
      toast.success("Thank you — we'll review and publish your quote shortly");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't submit"); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-accent/30 bg-accent/5 p-6" data-testid="share-experience-card">
      <div className="flex items-start gap-3 mb-3">
        <Quote className="w-5 h-5 text-accent mt-0.5" />
        <div>
          <p className="font-display font-bold">Congratulations — would you share a quick quote?</p>
          <p className="text-sm text-muted-foreground">{info.trigger === "inspection" ? "A great inspection result deserves a shout-out." : "A strong mock-interview score is worth celebrating."} One or two sentences about how Futuregen Innovations helped would mean a lot to other managers.</p>
        </div>
      </div>
      {info.submitted ? (
        <p className="text-sm inline-flex items-center gap-2 text-accent font-medium" data-testid="share-experience-thanks"><CheckCircle2 className="w-4 h-4" /> Thank you — your quote is with us for review.</p>
      ) : (
        <form onSubmit={submit} className="grid gap-3" data-testid="share-experience-form">
          <Textarea value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} required minLength={20} maxLength={600} rows={3}
            placeholder="e.g. The mock interview felt like the real thing — I walked into my CQC interview knowing exactly what to expect." data-testid="share-quote-input" />
          <div className="grid sm:grid-cols-3 gap-3">
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="Your name (as shown)" data-testid="share-name-input" />
            <Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="Role" data-testid="share-role-input" />
            <Input value={form.org} onChange={(e) => setForm({ ...form, org: e.target.value })} placeholder="Organisation (optional)" data-testid="share-org-input" />
          </div>
          <div><Button type="submit" disabled={busy} data-testid="share-submit-button">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit my quote"}</Button></div>
        </form>
      )}
    </div>
  );
}
